// ReviewManagerDashboard.jsx
import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Chip,
  Stack,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Rating,
  LinearProgress,
  Avatar,
  Divider,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton as MuiIconButton,
  FormControlLabel,
  Radio,
  RadioGroup,
  InputAdornment,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import { PieChart, pieArcLabelClasses } from '@mui/x-charts/PieChart';
import StarIcon from '@mui/icons-material/Star';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ReplyIcon from '@mui/icons-material/Reply';
import config from '../../twh/config';

// PHP-like rating color logic in JS
const getRatingColor = (percentage) => {
  if (percentage >= 75) return 'rgba(0, 128, 0, 0.8)';
  if (percentage >= 50) return 'rgba(128, 128, 128, 0.8)';
  return 'rgba(255, 69, 0, 0.8)';
};

// Stop words (same as in your PHP)
const stopWords = [
  'i','me','my','myself','we','our','ours','ourselves','you','your','yours',
  'he','him','his','himself','she','her','hers','herself','it','its','itself',
  'they','them','their','theirs','themselves','what','which','who','whom','this',
  'that','these','those','am','is','are','was','were','be','been','being','have',
  'has','had','having','do','does','did','doing','a','an','the','and','but','if',
  'or','because','as','until','while','of','at','by','for','with','about','against',
  'between','into','through','during','before','after','above','below','to','from',
  'up','down','in','out','on','off','over','under','again','further','then','once',
  'here','there','when','where','why','how','all','any','both','each','few','more',
  'most','other','some','such','no','nor','not','only','own','same','so','than',
  'too','very','can','will','just','don','should','now'
];

function ReviewManagerDashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Main stats
  const [overallPercentage, setOverallPercentage] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);
  const [positiveCount, setPositiveCount] = useState(0);
  const [neutralCount, setNeutralCount] = useState(0);
  const [negativeCount, setNegativeCount] = useState(0);
  const [averageRating, setAverageRating] = useState(0);
  // Reviews list for table
  const [reviews, setReviews] = useState([]);
  // Top words
  const [topWords, setTopWords] = useState([]);
  // Competitor states
  const [competitor, setCompetitor] = useState(null);
  const [showAddForm, setShowAddForm] = useState(true);
  const [competitorName, setCompetitorName] = useState('');
  const [selectedPlaceId, setSelectedPlaceId] = useState('');
  const [competitorId, setCompetitorId] = useState(null);
  // UI states
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedReview, setSelectedReview] = useState(null);
  const [openKeywordsModal, setOpenKeywordsModal] = useState(false);
  const [sortBy, setSortBy] = useState("");
  // Reply form states
  const [replyText, setReplyText] = useState('');
  const [tone, setTone] = useState('informeel');
  const [maxWords, setMaxWords] = useState(150);
  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  // Loading states
  const [generatingReply, setGeneratingReply] = useState(false);
  const [addingCompetitor, setAddingCompetitor] = useState(false);
  const [updatingReviews, setUpdatingReviews] = useState(false);

  const locationInputRef = useRef(null);
  const openMenu = Boolean(anchorEl);

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleModalClose = () => {
    setSelectedReview(null);
    setReplyText('');
  };
  const handleKeywordsOpen = () => setOpenKeywordsModal(true);
  const handleKeywordsClose = () => setOpenKeywordsModal(false);
  const handleSnackbarClose = () => setSnackbar({ ...snackbar, open: false });

  // Load Google Maps script
  useEffect(() => {
    if (window.google?.maps?.places && locationInputRef.current) {
      initAutocomplete();
      return;
    }
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${config.googleAddressApiKey || 'YOUR_API_KEY_HERE'}&libraries=places&v=weekly`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      console.log("Google Maps script loaded");
      if (locationInputRef.current) initAutocomplete();
    };
    script.onerror = () => {
      setSnackbar({ open: true, message: "Failed to load Google Places API", severity: 'error' });
    };
    document.head.appendChild(script);
  }, []);

  const initAutocomplete = () => {
    if (!window.google?.maps?.places || !locationInputRef.current) return;
    const autocomplete = new window.google.maps.places.Autocomplete(locationInputRef.current, {
      fields: ['place_id', 'name', 'formatted_address', 'rating'],
      types: ['establishment'],
    });
    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (!place.place_id) {
        setSnackbar({ open: true, message: "No valid place selected", severity: 'warning' });
        return;
      }
      setSelectedPlaceId(place.place_id);
      setCompetitorName(place.name || place.formatted_address || '');
    });
  };

  // Load main data + competitor
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // 1. Own location stats
        const locRes = await fetch(config.reactApi_baseUrl + '/react-api/reviews-manager/google-my-business-location.php', {
          credentials: 'include'
        });
        if (!locRes.ok) throw new Error('Location fetch failed');
        const locJson = await locRes.json();
        if (!locJson.status || !locJson.data?.length) throw new Error('No location data');
        const location = locJson.data[0];
        const locData = JSON.parse(location.jsonContent || '{}');
        const avgRating = parseFloat(locData.averageRating || 0);
        const totalReviewCount = locData.totalReviewCount || 0;
        setAverageRating(avgRating);
        setTotalReviews(totalReviewCount);
        setOverallPercentage(Math.round((avgRating / 5) * 100));

        // 2. All reviews → stats + word cloud
        const reviewsRes = await fetch(
          `${config.reactApi_baseUrl}/react-api/reviews-manager/google-my-business-reviews.php?gmb_location_id=${location.id}`,
          { credentials: 'include' }
        );
        if (!reviewsRes.ok) throw new Error('Reviews fetch failed');
        const reviewsJson = await reviewsRes.json();
        if (!reviewsJson.status || !reviewsJson.data) throw new Error('No reviews data');

        const processedReviews = reviewsJson.data.map(item => {
          const r = JSON.parse(item.jsonContent || '{}');
          let ratingNum = 0, sentiment = 'Neutral', percent = 0;
          switch (r.starRating) {
            case 'FIVE': ratingNum = 5; sentiment = 'Positive'; percent = 100; break;
            case 'FOUR': ratingNum = 4; sentiment = 'Positive'; percent = 80; break;
            case 'THREE': ratingNum = 3; sentiment = 'Neutral'; percent = 60; break;
            case 'TWO': ratingNum = 2; sentiment = 'Negative'; percent = 40; break;
            case 'ONE': ratingNum = 1; sentiment = 'Negative'; percent = 20; break;
            default: break;
          }
          return {
            platform: 'G',
            name: r.reviewer?.displayName || 'Anonymous',
            date: new Date(r.createTime).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-'),
            text: r.comment || '',
            rating: ratingNum,
            percent,
            sentiment,
            reviewId: r.reviewId || null,
          };
        });

        setPositiveCount(processedReviews.filter(r => r.sentiment === 'Positive').length);
        setNeutralCount(processedReviews.filter(r => r.sentiment === 'Neutral').length);
        setNegativeCount(processedReviews.filter(r => r.sentiment === 'Negative').length);

        // Word cloud logic
        const allText = processedReviews.map(r => r.text.toLowerCase()).join(' ');
        const cleanText = allText.replace(/[^a-z\s]/gi, ' ');
        const words = cleanText.split(/\s+/).filter(w => w.length > 0);
        const filtered = words.filter(w => !stopWords.includes(w));
        const countMap = {};
        filtered.forEach(w => countMap[w] = (countMap[w] || 0) + 1);
        const repeated = Object.entries(countMap)
          .filter(([, count]) => count > 1)
          .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
        setTopWords(repeated);

        // 3. Competitor data
        const compRes = await fetch(config.reactApi_baseUrl + '/react-api/reviews-manager/google-my-business-competitor.php', {
          credentials: 'include'
        });
        const compJson = await compRes.json();
        if (compJson.status && compJson.data?.length > 0) {
          const compItem = compJson.data[0];
          const compData = JSON.parse(compItem.jsonContent || '{}');
          const compRating = parseFloat(compData.rating || 0);
          setCompetitor({
            title: compItem.title || compData.displayName?.text || 'Unknown',
            rating: compRating.toFixed(1),
            satisfaction: Math.round((compRating / 5) * 100)
          });
          setCompetitorId(compItem.id);
          setShowAddForm(false);
        }
      } catch (err) {
        setError(err.message);
        setSnackbar({ open: true, message: err.message, severity: 'error' });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Load sorted reviews when sortBy changes
  useEffect(() => {
    const fetchSortedReviews = async () => {
      try {
        const locRes = await fetch(config.reactApi_baseUrl + '/react-api/reviews-manager/google-my-business-location.php', { credentials: 'include' });
        const locJson = await locRes.json();
        if (!locJson.status || !locJson.data?.length) return;
        const locationId = locJson.data[0].id;

        let url = `${config.reactApi_baseUrl}/react-api/reviews-manager/google-my-business-reviews.php?gmb_location_id=${locationId}`;
        url += sortBy ? `&sort-by=${sortBy}` : `&sort-by=all`;

        const res = await fetch(url, { credentials: 'include' });
        if (!res.ok) throw new Error('Sorted reviews failed');
        const json = await res.json();
        if (!json.status || !json.data) {
          setReviews([]);
          return;
        }

        const tableReviews = json.data.map(item => {
          const r = JSON.parse(item.jsonContent || '{}');
          let ratingNum = 0, sentiment = 'Neutral', percent = 0;
          switch (r.starRating) {
            case 'FIVE': ratingNum = 5; sentiment = 'Positive'; percent = 100; break;
            case 'FOUR': ratingNum = 4; sentiment = 'Positive'; percent = 80; break;
            case 'THREE': ratingNum = 3; sentiment = 'Neutral'; percent = 60; break;
            case 'TWO': ratingNum = 2; sentiment = 'Negative'; percent = 40; break;
            case 'ONE': ratingNum = 1; sentiment = 'Negative'; percent = 20; break;
          }
          return {
            platform: 'G',
            name: r.reviewer?.displayName || 'Anonymous',
            date: new Date(r.createTime).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-'),
            text: r.comment || '',
            rating: ratingNum,
            percent,
            sentiment,
            reviewId: r.reviewId || null,
          };
        });

        setReviews(tableReviews);
      } catch (err) {
        setSnackbar({ open: true, message: "Could not load reviews", severity: 'error' });
      }
    };
    if (!loading) fetchSortedReviews();
  }, [sortBy, loading]);

  // Handle "Review Updates" button click
  const handleReviewUpdates = async () => {
    setUpdatingReviews(true);
    setSnackbar({ open: true, message: "Updating reviews from Google...", severity: 'info' });

    try {
      const res = await fetch(config.reactApi_baseUrl + '/react-api/reviews-manager/google-my-business-reviewsUpdates.php', {
        method: 'GET', // or 'POST' if your API requires it
        credentials: 'include',
      });

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      //const json = await res.json();

      setSnackbar({ open: true, message: "Reviews updated successfully! Refreshing...", severity: 'success' });
        // Wait a tiny bit so user can see success message
      setTimeout(() => {
          window.location.reload();
      }, 1200);
     
    } catch (err) {
      setSnackbar({ open: true, message: `Update failed: ${err.message}`, severity: 'error' });
    } finally {
      setUpdatingReviews(false);
    }
  };

  // Add competitor
  const handleAddCompetitor = async () => {
    if (!selectedPlaceId || !competitorName.trim()) {
      setSnackbar({ open: true, message: 'Please select location and enter name', severity: 'warning' });
      return;
    }
    setAddingCompetitor(true);
    try {
      const formData = new FormData();
      formData.append('issetSubmitCompetitor', 'true');
      formData.append('place_id', selectedPlaceId);
      formData.append('title', competitorName);

      const res = await fetch(config.reactApi_baseUrl + '/react-api/reviews-manager/google-my-business-competitor-update.php', {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });
      const json = await res.json();

      if (json.status) {
        const compRes = await fetch(config.reactApi_baseUrl + '/react-api/reviews-manager/google-my-business-competitor.php', { credentials: 'include' });
        const compJson = await compRes.json();
        if (compJson.status && compJson.data?.length > 0) {
          const compItem = compJson.data[0];
          const compData = JSON.parse(compItem.jsonContent || '{}');
          const compRating = parseFloat(compData.rating || 0);
          setCompetitor({
            title: compItem.title || compData.displayName?.text || competitorName,
            rating: compRating.toFixed(1),
            satisfaction: Math.round((compRating / 5) * 100),
          });
          setCompetitorId(compItem.id);
          setShowAddForm(false);
          setSnackbar({ open: true, message: 'Competitor added successfully', severity: 'success' });
        }
      } else {
        setSnackbar({ open: true, message: json.errorMsg?.join(', ') || 'Failed to add competitor', severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setAddingCompetitor(false);
    }
  };

  // Delete competitor
  const handleDeleteClick = () => setDeleteDialogOpen(true);

  const handleConfirmDelete = async () => {
    if (!competitorId) return;
    try {
      const formData = new FormData();
      formData.append('issetDeleteCompetitor', 'true');
      formData.append('competitor_id', competitorId);

      const res = await fetch(config.reactApi_baseUrl + '/react-api/reviews-manager/google-my-business-competitor-delete.php', {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });
      const json = await res.json();

      if (json.status) {
        setCompetitor(null);
        setCompetitorId(null);
        setShowAddForm(true);
        setDeleteDialogOpen(false);
        setSnackbar({ open: true, message: 'Competitor removed', severity: 'success' });
      } else {
        setSnackbar({ open: true, message: json.errorMsg?.join(', ') || 'Delete failed', severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const handleCancelDelete = () => setDeleteDialogOpen(false);

  // Generate AI Reply
  const handleCreateReply = async () => {
    if (!selectedReview?.text) {
      setSnackbar({ open: true, message: "No review text available", severity: 'warning' });
      return;
    }
    setGeneratingReply(true);
    setSnackbar({ open: true, message: "Generating reply...", severity: 'info' });

    try {
      const formData = new FormData();
      formData.append('issetReplayChatGPT', 'true');
      formData.append('review', selectedReview.text);
      formData.append('type', tone);
      formData.append('maxWord', maxWords);

      const res = await fetch(config.reactApi_baseUrl + '/react-api/reviews-manager/google-reviews-chatgpt-reply.php', {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });
      const json = await res.json();

      if (json.chatGpt?.status === "true" && json.chatGpt?.msg) {
        setReplyText(json.chatGpt.msg);
        setSnackbar({ open: true, message: "Reply generated!", severity: 'success' });
      } else {
        setSnackbar({
          open: true,
          message: json.chatGpt?.error || "Failed to generate reply",
          severity: 'error'
        });
      }
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setGeneratingReply(false);
    }
  };

  // Submit reply
  const handleSubmitReply = async () => {
    if (!selectedReview?.reviewId) {
      setSnackbar({ open: true, message: "Missing review ID", severity: 'error' });
      return;
    }
    if (!replyText.trim()) {
      setSnackbar({ open: true, message: "Reply cannot be empty", severity: 'warning' });
      return;
    }

    try {
      const formData = new FormData();
      formData.append('issetReplaySubmit', 'true');
      formData.append('reviewId', selectedReview.reviewId);
      formData.append('replyMsg', replyText);

      const res = await fetch(config.reactApi_baseUrl + '/react-api/reviews-manager/google-reviews-chatgpt-reply.php', {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });
      const json = await res.json();

      if (json.replySubmit?.status) {
        setSnackbar({ open: true, message: "Reply submitted successfully!", severity: 'success' });
        handleModalClose();
      } else {
        setSnackbar({
          open: true,
          message: json.replySubmit?.errorMsg?.join(', ') || "Failed to submit reply",
          severity: 'error'
        });
      }
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(replyText);
    setSnackbar({ open: true, message: 'Copied to clipboard!', severity: 'success' });
  };

  const pieData = [
    { id: 0, value: positiveCount, label: `Positive ${positiveCount}`, color: '#008000' },
    { id: 1, value: neutralCount, label: `Neutral ${neutralCount}`, color: '#808080' },
    { id: 2, value: negativeCount, label: `Negative ${negativeCount}`, color: '#FF4500' },
  ];

  const shownTags = topWords.slice(0, 5).map(([word]) => word);
  const hasMoreTags = topWords.length > 5;

  if (loading) return <Typography>Loading review data...</Typography>;
  if (error) return <Typography color="error">Error: {error}</Typography>;

  return (
    <Box>
      <Grid container spacing={3} sx={{ mb: 2 }}>
        <Grid size={{ md: 6, sm: 6 }}>
          <Typography variant="h4" fontWeight={800} gutterBottom>
            REVIEW MANAGER
          </Typography>
        </Grid>
        <Grid size={{ md: 6, sm: 6 }} sx={{ textAlign: 'right' }}>
          <Button
            variant="contained"
            startIcon={updatingReviews ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
            disabled={updatingReviews}
            onClick={handleReviewUpdates}
            sx={{
              bgcolor: '#FF2C6D',
              '&:hover': { bgcolor: '#d91e5a' },
              minWidth: 160,
            }}
          >
            {updatingReviews ? 'Updating...' : 'Review Updates'}
          </Button>
        </Grid>
      </Grid>

      {/* Rest of your layout remains exactly the same */}
      <Grid container spacing={3} sx={{ mb: 2 }}>
        <Grid size={{ md: 5, sm: 12 }}>
          <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3 }}>
            <Typography variant="h5" fontWeight={800} sx={{ color: '#ff2c6d', mb: 2 }}>
              RATING
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="h5" fontWeight="800" sx={{ color: '#0718AB', mb: 2 }}>
              {overallPercentage}%
            </Typography>
            <Box sx={{ height: 220, width: '100%' }}>
              <PieChart
                series={[{
                  data: pieData,
                  innerRadius: 60,
                  outerRadius: 100,
                  arcLabel: (item) => (item.value > 0 ? `${item.label}` : ''),
                  arcLabelMinAngle: 45,
                }]}
                sx={{ [`& .${pieArcLabelClasses.root}`]: { fill: 'white', fontWeight: 'bold' } }}
              />
            </Box>
          </Paper>
        </Grid>

        <Grid size={{ md: 7, sm: 12 }}>
          <Grid container spacing={3} sx={{ mb: 2 }}>
            <Grid size={{ md: 4, sm: 4 }}>
              <Paper sx={{ p: 2, borderRadius: 3, boxShadow: 3 }}>
                <Typography variant="h5" fontWeight="800" sx={{ color: '#0718AB', mb: 1 }}>
                  {positiveCount}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '12px' }}>POSITIVE REVIEWS</Typography>
              </Paper>
            </Grid>
            <Grid size={{ md: 4, sm: 4 }}>
              <Paper sx={{ p: 2, borderRadius: 3, boxShadow: 3 }}>
                <Typography variant="h5" fontWeight="800" sx={{ color: '#0718AB', mb: 1 }}>
                  {neutralCount}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '12px' }}>NEUTRAL REVIEWS</Typography>
              </Paper>
            </Grid>
            <Grid size={{ md: 4, sm: 4 }}>
              <Paper sx={{ p: 2, borderRadius: 3, boxShadow: 3 }}>
                <Typography variant="h5" fontWeight="800" sx={{ color: '#FF3F78', mb: 1 }}>
                  {negativeCount}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '12px' }}>NEGATIVE REVIEWS</Typography>
              </Paper>
            </Grid>
          </Grid>

          <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3 }}>
            <Typography variant="h5" fontWeight={800} sx={{ color: '#ff2c6d', mb: 2 }}>
              COMPETITOR INSIGHT
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container>
              <Grid
                size={{ md: 6, sm: 12 }}
                sx={{
                  pr: { md: 2, sm: 0 },
                  borderRight: { md: 1, sm: 0 },
                  borderColor: "#dddddd !important"
                }}
              >
                <Typography variant="subtitle1" fontWeight="bold" sx={{fontSize:'12px'}}>
                  TechWare House
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1} mt={1}>
                  <Typography variant="h5">{averageRating.toFixed(1)} / 5</Typography>
                  <StarIcon color="primary" />
                </Stack>
                <Typography variant="body2" color="text.secondary" mt={1} sx={{fontSize:'12px'}}>
                  Satisfaction
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={overallPercentage}
                  color="success"
                  sx={{ height: 10, borderRadius: 5, mt: 1 }}
                />
                <Typography variant="body2" textAlign="right" mt={0.5}>
                  {overallPercentage}%
                </Typography>
              </Grid>

              <Grid size={{ md: 6, sm: 12 }} sx={{ pl: { md: 2, sm: 0 } }}>
                {showAddForm ? (
                  <>
                    <Typography variant="subtitle2" gutterBottom sx={{fontSize:'12px', fontWeight:'bold'}}>
                      Add Competitor
                    </Typography>
                    <TextField
                      inputRef={locationInputRef}
                      label="Search Competitor / Enter a location"
                      fullWidth
                      variant="outlined"
                      size="small"
                      sx={{ mb: 1 }}
                    />
                    <TextField
                      label="Name"
                      fullWidth
                      variant="outlined"
                      size="small"
                      value={competitorName}
                      onChange={(e) => setCompetitorName(e.target.value)}
                      sx={{ mb: 1 }}
                    />
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      color="error"
                      disabled={addingCompetitor}
                      onClick={handleAddCompetitor}
                      sx={{fontSize:'12px'}}
                    >
                      {addingCompetitor ? "Adding..." : "Add Competitor"}
                    </Button>
                  </>
                ) : (
                  <>
                    <Grid sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{fontSize:'12px', flex: 1}}>
                        {competitor.title}
                      </Typography>
                      <IconButton
                        size="small"
                        sx={{ color: '#D32F2F' }}
                        onClick={handleDeleteClick}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Grid>
                    <Stack direction="row" alignItems="center" spacing={1} mt={1}>
                      <Typography variant="h5">{competitor.rating} / 5</Typography>
                      <StarIcon color="primary" />
                    </Stack>
                    <Typography variant="body2" color="text.secondary" mt={1} sx={{fontSize:'12px'}}>
                      Satisfaction
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={competitor.satisfaction}
                      color="success"
                      sx={{ height: 10, borderRadius: 5, mt: 1 }}
                    />
                    <Typography variant="body2" textAlign="right" mt={0.5}>
                      {competitor.satisfaction}%
                    </Typography>
                  </>
                )}
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCancelDelete} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete "{competitor?.title}"?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Platform Summary Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 3, mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Platforms</TableCell>
              <TableCell align="left">Rating</TableCell>
              <TableCell align="left">Percentage</TableCell>
              <TableCell align="left">Reviews</TableCell>
              <TableCell align="left"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell><Chip label="Google.com" color="primary" size="small" /></TableCell>
              <TableCell align="left">{averageRating.toFixed(1)}/5</TableCell>
              <TableCell align="left">{overallPercentage}%</TableCell>
              <TableCell align="left">{totalReviews}</TableCell>
              <TableCell align="right">
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {shownTags.map(tag => (
                    <Chip key={tag} label={tag} size="small" color="default" variant="outlined" />
                  ))}
                  {hasMoreTags && (
                    <Chip
                      label={`+${topWords.length - 5}`}
                      size="small"
                      color="default"
                      variant="outlined"
                      onClick={handleKeywordsOpen}
                      sx={{ cursor: 'pointer' }}
                    />
                  )}
                </Stack>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Reviews Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Platform</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Reviews</TableCell>
              <TableCell align="left" colSpan={2}>
                <FormControl size="small" sx={{ width: '100%' }}>
                  <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="positive">Positive</MenuItem>
                    <MenuItem value="neutral">Neutral</MenuItem>
                    <MenuItem value="negative">Negative</MenuItem>
                  </Select>
                </FormControl>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reviews.map((review, idx) => (
              <TableRow key={idx} hover>
                <TableCell>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                    {review.platform}
                  </Avatar>
                </TableCell>
                <TableCell>{review.name}</TableCell>
                <TableCell>{review.date}</TableCell>
                <TableCell sx={{ maxWidth: 400, whiteSpace: 'pre-wrap' }}>
                  {review.text || <em>No comment</em>}
                </TableCell>
                <TableCell align="right">
                  <Box display="flex" alignItems="center" justifyContent="flex-end">
                    <Rating value={review.rating} precision={0.5} readOnly size="small" />
                    <Typography variant="body2" ml={1}>
                      {review.percent}%
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    onClick={(e) => {
                      setSelectedReview(review);
                      handleMenuOpen(e);
                    }}
                    size="small"
                  >
                    <ReplyIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}

            {reviews.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                  No reviews found for selected filter
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Review Reply Modal */}
      <Dialog
        open={!!selectedReview}
        onClose={handleModalClose}
        maxWidth="sm"
        fullWidth
        sx={{ '& .MuiDialog-paper': { borderRadius: 3 } }}
      >
        {selectedReview && (
          <>
            <DialogTitle sx={{
              bgcolor: '#fff',
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: '1px solid #eee'
            }}>
              <Typography variant="h6" fontWeight="bold" sx={{ color: '#ff2c6d' }}>
                REVIEW DETAILS
              </Typography>
              <MuiIconButton
                onClick={handleModalClose}
                sx={{ bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}
                size="small"
              >
                <CloseIcon fontSize="small" />
              </MuiIconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ bgcolor: '#f8f9fa' }}>
              <Typography variant="h6" fontWeight="bold" color="#0718AB" gutterBottom>
                {selectedReview.name}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2, gap: 2 }}>
                <Box sx={{ minWidth: 100, textAlign: 'center' }}>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{fontSize:'12px'}}>
                    {selectedReview.date}
                  </Typography>
                  <Rating
                    value={selectedReview.rating}
                    readOnly
                    precision={0.5}
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{fontSize:'11px'}}>
                    {selectedReview.percent}%
                  </Typography>
                </Box>

                <Typography variant="body1" sx={{ fontStyle: 'italic', fontSize: '13px', lineHeight: 1.4 }}>
                  {selectedReview.text || <em>No comment provided</em>}
                </Typography>
              </Box>

              <Divider sx={{ my: 2.5 }} />

              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" fontWeight="bold" color="#0718AB" gutterBottom sx={{fontSize:'13px'}}>
                    Answer this review
                  </Typography>
                  <TextField
                    multiline
                    rows={6}
                    fullWidth
                    variant="outlined"
                    placeholder="Type your reply here or generate with AI..."
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    disabled={generatingReply}
                    sx={{ bgcolor: 'white' }}
                  />
                </Box>

                <Box sx={{ width: { md: 240, xs: '100%' } }}>
                  <Typography variant="h6" fontWeight="bold" color="#0718AB" gutterBottom sx={{fontSize:'13px'}}>
                    Settings
                  </Typography>

                  <FormControl component="fieldset" sx={{ mb: 2 }}>
                    <RadioGroup value={tone} onChange={e => setTone(e.target.value)}>
                      <FormControlLabel value="informeel" control={<Radio />} label="Informeel" />
                      <FormControlLabel value="formeel" control={<Radio />} label="Formeel" />
                    </RadioGroup>
                  </FormControl>

                  <Typography variant="subtitle1" gutterBottom sx={{fontSize:'12px'}}>
                    Max woorden
                  </Typography>
                  <TextField
                    type="number"
                    value={maxWords}
                    onChange={e => setMaxWords(Math.max(0, Number(e.target.value) || 0))}
                    inputProps={{ min: 0, max: 500 }}
                    fullWidth
                    size="small"
                    disabled={generatingReply}
                    sx={{ mb: 2, bgcolor: 'white' }}
                    InputProps={{ endAdornment: <InputAdornment position="end">max</InputAdornment> }}
                  />

                  <Button
                    variant="contained"
                    fullWidth
                    disabled={generatingReply}
                    sx={{ bgcolor: '#ff2c6d', py: 1.2 }}
                    onClick={handleCreateReply}
                  >
                    {generatingReply ? "Generating..." : "Create AI Reply"}
                  </Button>
                </Box>
              </Box>

              <Box sx={{ mt: 3, textAlign: 'right' }}>
                <Button
                  variant="outlined"
                  startIcon={<ContentCopyIcon />}
                  onClick={handleCopy}
                  disabled={!replyText.trim()}
                  sx={{ mr: 1.5 }}
                >
                  Copy
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmitReply}
                  disabled={!replyText.trim() || generatingReply}
                >
                  Submit Reply
                </Button>
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>

      {/* Keywords Modal */}
      <Dialog
        open={openKeywordsModal}
        onClose={handleKeywordsClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#f8f9fa' }}>
          <Typography variant="h6" fontWeight="bold" color="#ff2c6d">
            ONDERWERPEN / TREFWOORDEN
          </Typography>
          <MuiIconButton onClick={handleKeywordsClose} size="small">
            <CloseIcon />
          </MuiIconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <TableContainer>
            <Table size="small">
              <TableBody>
                {topWords.map(([word, count]) => (
                  <TableRow key={word}>
                    <TableCell sx={{ pl: 4, fontWeight: 500 }}>{word}</TableCell>
                    <TableCell align="right" sx={{ pr: 4, color: 'text.secondary' }}>{count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default ReviewManagerDashboard;